import { FaArrowDown, FaArrowUp, FaBoxOpen, FaEye, FaFilePdf, FaPaste, FaPencilAlt, FaPlus, FaSave, FaSpinner, FaSyncAlt, FaTimes } from 'react-icons/fa';
import styles from './BudgetLineMenu.module.scss';
import { IoCopyOutline, IoTrash, IoMove, IoCutOutline, IoDuplicateOutline } from 'react-icons/io5';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { AddLinePlacement } from 'store/budget/type';
import { BODY_TOOLTIP } from 'Config';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { allSelectedAtSameDepth, allSelectedShareParent, canMoveLinesDown, canMoveLinesUp } from 'store/budget/reducer';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { LinesDataProvider } from '../LinesDataProvider';

interface AddLineMenuItem {
    label: string;
    lineType: BudgetLineType;
    placement: AddLinePlacement;
}

interface Props {
    showAdd: boolean;
    showClone: boolean;
    showCopy: boolean;
    showCut: boolean;
    showPaste: boolean;
    showPreview: boolean;
    showRemove: boolean;
    showEditCancel: boolean;
    showToggleDataSheet: boolean;
    isEditing: boolean;
    showEdit: boolean;
    isEnableDataSheet: boolean;
    isLoading: boolean;
    level: number;
    handleProps?: any;
    showMoveUp: boolean;
    showMoveDown: boolean;
    showMove: boolean;
    showRefresh: boolean;
    showSaveAsComponent: boolean;
    showImportComponent: boolean;
    line: BudgetLineDto;
    disabled: boolean;
    selectionCount: number;
    onAdd: (lineType: BudgetLineType, placement: AddLinePlacement) => void;
    onClone: () => void;
    onCopy: () => void;
    onCut: () => void;
    onPaste: () => void;
    onPreview: () => void;
    onRemove: () => void;
    onEdit: (v: boolean) => void;
    onToggleDataSheet: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onRefresh: () => void;
    onSaveAsComponent: () => void;
    onImportComponent: (placement: AddLinePlacement) => void;
}

function BudgetLineMenu({
    showAdd, onAdd, showClone, onClone, showCopy, onCopy, showCut, onCut, showPaste, onPaste,
    showPreview, onPreview, showRemove, onRemove, showEdit, isEditing, onEdit,
    onToggleDataSheet, showToggleDataSheet, isEnableDataSheet,
    level, showEditCancel, handleProps, showMoveUp, showMoveDown, showMove,
    onMoveDown, onMoveUp, line, disabled, showRefresh, onRefresh, isLoading,
    showSaveAsComponent, showImportComponent, onSaveAsComponent, onImportComponent,
    selectionCount,
}: Props): JSX.Element | null {
    const { t } = useTranslation();
    const { isComponentEditor } = useContext(LinesDataProvider);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const isSubsection = level > 0;
    const isLockedRootSectionChild = (isComponentEditor ?? false) && level === 1;
    const isMultiSelect = selectionCount > 1;
    const sectionsShareParent = useSelector<Reducers, boolean>(x => allSelectedShareParent(x.budget.lines, x.budget.selectedLineKeys));
    const sectionsSameDepth = useSelector<Reducers, boolean>(x => allSelectedAtSameDepth(x.budget.lines, x.budget.selectedLineKeys));
    const disableMoveUp = useSelector<Reducers, boolean>(x => showMoveUp && !canMoveLinesUp(x.budget.lines, x.budget.selectedLineKeys.length ? x.budget.selectedLineKeys : [line.key]));
    const disableMoveDown = useSelector<Reducers, boolean>(x => showMoveDown && !canMoveLinesDown(x.budget.lines, x.budget.selectedLineKeys.length ? x.budget.selectedLineKeys : [line.key]));
    const disableMoveArrows = !sectionsShareParent;
    const disableDragHandle = !sectionsSameDepth;
    const isBeingCut = useSelector<Reducers, boolean>(x => x.budget.copyType === 'cut' && x.budget.copiedLineKeys.includes(line.key));
    const isBeingCopied = useSelector<Reducers, boolean>(x => x.budget.copyType === 'copy' && x.budget.copiedLineKeys.includes(line.key));
    const crossSectionTooltip = t('budgets.budget.lines.floating_menu.move_cross_section_disabled');

    const onAddLine = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, menuItem: AddLineMenuItem) => {
        e.stopPropagation();

        onAdd(menuItem.lineType, menuItem.placement);
        setShowAddMenu(false);
    }

    const canHaveChildren = line.lineType === BudgetLineType.SECTION || line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT || line.lineType === BudgetLineType.COMPONENT;

    const addMenuOptions: AddLineMenuItem[] = [
        // Element options
        ...(isSubsection
            ? [
                ...(canHaveChildren
                    ? [
                        {
                            label: t('budgets.budget.lines.add_options.element') + ' ' + t('budgets.budget.lines.add_options.inside_level'),
                            lineType: BudgetLineType.ELEMENT,
                            placement: AddLinePlacement.INSIDE,
                        },
                    ]
                    : []),
                {
                    label: t('budgets.budget.lines.add_options.element') + ' ' + t('budgets.budget.lines.add_options.same_level'),
                    lineType: BudgetLineType.ELEMENT,
                    placement: AddLinePlacement.SAME,
                },
            ]
            : []),
        ...(!isSubsection
            ? [{
                label: t('budgets.budget.lines.add_options.element'),
                lineType: BudgetLineType.ELEMENT,
                placement: AddLinePlacement.INSIDE,
            }]
            : []),
        // Component options
        ...(isSubsection
            ? [
                ...(canHaveChildren
                    ? [
                        {
                            label: t('budgets.budget.lines.add_options.component') + ' ' + t('budgets.budget.lines.add_options.inside_level'),
                            lineType: BudgetLineType.COMPONENT_ELEMENT,
                            placement: AddLinePlacement.INSIDE,
                        },
                    ]
                    : []),
                {
                    label: t('budgets.budget.lines.add_options.component') + ' ' + t('budgets.budget.lines.add_options.same_level'),
                    lineType: BudgetLineType.COMPONENT_ELEMENT,
                    placement: AddLinePlacement.SAME,
                },
            ]
            : []),
        ...(!isSubsection
            ? [{
                label: t('budgets.budget.lines.add_options.component'),
                lineType: BudgetLineType.COMPONENT_ELEMENT,
                placement: AddLinePlacement.INSIDE,
            }]
            : []),
        // Subsection options
        ...(!isSubsection
            ? [{
                label: t('budgets.budget.lines.add_options.subsection'),
                lineType: BudgetLineType.SECTION,
                placement: AddLinePlacement.INSIDE,
            }]
            : []),
        ...(isSubsection
            ? [
                ...(canHaveChildren
                    ? [
                        {
                            label: t('budgets.budget.lines.add_options.subsection') + ' ' + t('budgets.budget.lines.add_options.inside_level'),
                            lineType: BudgetLineType.SECTION,
                            placement: AddLinePlacement.INSIDE,
                        },
                    ]
                    : []),
                {
                    label: t('budgets.budget.lines.add_options.subsection') + ' ' + t('budgets.budget.lines.add_options.same_level'),
                    lineType: BudgetLineType.SECTION,
                    placement: AddLinePlacement.SAME,
                },
            ]
            : []),
        // Section (root) - not available in component editor
        ...(!isComponentEditor
            ? [{
                label: t('budgets.budget.lines.add_options.section'),
                lineType: BudgetLineType.SECTION,
                placement: AddLinePlacement.ROOT,
            }]
            : []),
        // Note
        {
            label: t('budgets.budget.lines.add_options.note') + (!canHaveChildren ? (' ' + t('budgets.budget.lines.add_options.same_level')) : ''),
            lineType: BudgetLineType.NOTE,
            placement: canHaveChildren ? AddLinePlacement.INSIDE : AddLinePlacement.SAME,
        },
        // Page break (not available in component editor)
        ...(!isComponentEditor
            ? [{
                label: t('budgets.budget.lines.add_options.page_break') + (!canHaveChildren ? (' ' + t('budgets.budget.lines.add_options.same_level')) : ''),
                lineType: BudgetLineType.PAGE_BREAK,
                placement: canHaveChildren ? AddLinePlacement.INSIDE : AddLinePlacement.SAME,
            }]
            : []),

    ];

    const importComponentOptions: AddLineMenuItem[] = showImportComponent
        ? [
            ...(isSubsection && canHaveChildren
                ? [{
                    label: t('budgets.budget.lines.add_options.import_component') + ' ' + t('budgets.budget.lines.add_options.inside_level'),
                    lineType: BudgetLineType.COMPONENT_ELEMENT,
                    placement: AddLinePlacement.INSIDE,
                }]
                : []),
            ...(isSubsection
                ? [{
                    label: t('budgets.budget.lines.add_options.import_component') + ' ' + t('budgets.budget.lines.add_options.same_level'),
                    lineType: BudgetLineType.COMPONENT_ELEMENT,
                    placement: AddLinePlacement.SAME,
                }]
                : []),
            ...(!isSubsection
                ? [{
                    label: t('budgets.budget.lines.add_options.import_component'),
                    lineType: BudgetLineType.COMPONENT_ELEMENT,
                    placement: AddLinePlacement.INSIDE,
                }]
                : []),
        ]
        : [];

    const filteredAddMenuOptions = isLockedRootSectionChild
        ? addMenuOptions.filter(opt => opt.placement !== AddLinePlacement.SAME)
        : addMenuOptions;
    const filteredImportComponentOptions = isLockedRootSectionChild
        ? importComponentOptions.filter(opt => opt.placement !== AddLinePlacement.SAME)
        : importComponentOptions;

    return (
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
            <div className={styles.buttons}>
                {showMove && (
                    disableDragHandle
                        ? <button
                            className={`${styles.button} ${styles.handleButton}`}
                            type="button"
                            disabled
                            data-tooltip-id={BODY_TOOLTIP}
                            data-tooltip-content={crossSectionTooltip}
                        >
                            <IoMove />
                        </button>
                        : <button
                            className={`${styles.button} ${styles.handleButton}`}
                            type="button"
                            {...handleProps}
                        >
                            <IoMove />
                        </button>
                )}
                {showMoveUp && <button
                    className={styles.button}
                    type="button"
                    onClick={onMoveUp}
                    disabled={disableMoveUp || disableMoveArrows}
                    data-tooltip-id={disableMoveArrows ? BODY_TOOLTIP : undefined}
                    data-tooltip-content={disableMoveArrows ? crossSectionTooltip : undefined}
                >
                    <FaArrowUp />
                </button>}
                {showMoveDown && <button
                    className={styles.button}
                    type="button"
                    onClick={onMoveDown}
                    disabled={disableMoveDown || disableMoveArrows}
                    data-tooltip-id={disableMoveArrows ? BODY_TOOLTIP : undefined}
                    data-tooltip-content={disableMoveArrows ? crossSectionTooltip : undefined}
                >
                    <FaArrowDown />
                </button>}
                {showAdd && !isEditing && (
                    <button
                        className={`${styles.button} ${showAddMenu ? styles.active : ''}`}
                        type="button"
                        onClick={() => setShowAddMenu(s => !s)}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.lines.floating_menu.add')}
                    >
                        <FaPlus />
                    </button>
                )}
                {showClone && !isEditing && (
                    <button
                        className={styles.button}
                        type="button"
                        onClick={onClone}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.lines.floating_menu.clone')}
                    >
                        <IoDuplicateOutline />
                    </button>
                )}
                {showCopy && !isEditing && (
                    <button
                        className={`${styles.button} ${isBeingCopied ? styles.active : ''}`}
                        type="button"
                        onClick={onCopy}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={isBeingCopied ? t('budgets.budget.lines.floating_menu.being_copied') : t('budgets.budget.lines.floating_menu.copy', { count: Math.max(selectionCount, 1) })}
                    >
                        <IoCopyOutline />
                        {isMultiSelect && <span className={styles.badge}>{selectionCount}</span>}
                    </button>
                )}
                {showCut && !isEditing && (
                    <button
                        className={`${styles.button} ${isBeingCut ? styles.active : ''}`}
                        type="button"
                        onClick={onCut}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={isBeingCut ? t('budgets.budget.lines.floating_menu.being_cut') : t('budgets.budget.lines.floating_menu.cut', { count: Math.max(selectionCount, 1) })}
                    >
                        <IoCutOutline />
                        {isMultiSelect && <span className={styles.badge}>{selectionCount}</span>}
                    </button>
                )}
                {showPaste && !isEditing && (
                    <button
                        className={styles.button}
                        type="button"
                        onClick={onPaste}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.lines.floating_menu.paste')}
                    >
                        <FaPaste />
                    </button>
                )}
                {showPreview && !isEditing && (
                    <button
                        className={styles.button}
                        type="button"
                        onClick={onPreview}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.lines.floating_menu.preview')}
                    >
                        <FaEye />
                    </button>
                )}
                {(showToggleDataSheet && !isEditing) && (
                    <button
                        className={`${styles.button} ${isEnableDataSheet ? styles.active : ''} ${disabled ? styles.passiveDisabled : ''}`}
                        type="button"
                        onClick={!disabled ? onToggleDataSheet : undefined}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={isEnableDataSheet ? t('budgets.budget.lines.floating_menu.datasheet_visible') : t('budgets.budget.lines.floating_menu.datasheet_not_visible')}
                    >
                        <FaFilePdf />
                    </button>
                )}
                {showEdit && (
                    <button
                        className={`${styles.button} ${isEditing ? styles.active : ''}`}
                        type="button"
                        onClick={() => onEdit(true)}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.lines.floating_menu.edit')}
                    >
                        <FaPencilAlt />
                    </button>
                )}
                {showEditCancel && (
                    <button
                        className={styles.button}
                        type="button"
                        onClick={() => onEdit(false)}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.lines.floating_menu.cancel')}
                    >
                        <FaTimes />
                    </button>
                )}
                {showRefresh && (
                    <button
                        className={styles.button}
                        type="button"
                        onClick={onRefresh}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.lines.floating_menu.refresh')}
                    >
                        <FaSyncAlt />
                    </button>
                )}
                {showSaveAsComponent && !isEditing && (
                    <button
                        className={styles.button}
                        type="button"
                        onClick={onSaveAsComponent}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={line.sourceBudgetComponentId
                            ? t('budgets.budget.lines.floating_menu.update_component')
                            : t('budgets.budget.lines.floating_menu.save_as_component')}
                    >
                        <FaSave />
                    </button>
                )}
                {showRemove && (
                    <button
                        className={styles.button}
                        type="button"
                        onClick={onRemove}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.lines.floating_menu.remove', { count: Math.max(selectionCount, 1) })}
                    >
                        <IoTrash />
                        {isMultiSelect && <span className={styles.badge}>{selectionCount}</span>}
                    </button>
                )}
                {isLoading && (
                    <span className={styles.loading}>
                        <FaSpinner />
                    </span>
                )}
            </div>
            {showAddMenu && (
                <div className={styles.menu}>
                    {filteredAddMenuOptions.map((option, i) => (
                        <div key={i} className={styles.menuItem} onClick={(e) => onAddLine(e, option)}>
                            {option.label}
                        </div>
                    ))}
                    {filteredImportComponentOptions.length > 0 && (
                        <>
                            <div className={styles.menuDivider} />
                            {filteredImportComponentOptions.map((option, i) => (
                                <div key={`import-${i}`} className={styles.menuItem} onClick={(e) => {
                                    e.stopPropagation();
                                    onImportComponent(option.placement);
                                    setShowAddMenu(false);
                                }}>
                                    <FaBoxOpen style={{ marginRight: '0.4rem' }} />
                                    {option.label}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default BudgetLineMenu;
